import React, { useContext } from 'react';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { Button, Input, Label } from 'edozvola-frontend-lib/dist/ui';
import { Col, FormGroup, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { FormikDatepicker } from 'edozvola-frontend-lib/dist/ui/components/datepicker/FormikDatepicker';
import { useDispatch } from 'react-redux';
import { initCaseFormChange, setFormChanged } from 'app/shared/reducers/case-form-change';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { CaseTabContext } from '../case-form-wrapper';
import { fetchCaseRequestInfoQuery, saveCaseRequestInfoMutation } from './service/case-request-info-form.queries';
import { fetchNumberOfDaysToSolveCodebookQuery, fetchRequestAmendmentCodebookQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { caseRequestInfoValidation } from './model/case-request-info-form.schema';
import { CodebookModel } from 'app/shared/model/shared.model';
import FormikValueObserver from 'app/shared/formik-observer/formik-observer';
import { caseRequestInfoInitial, CaseRequestInfoModel } from 'app/edozvola/cases/case-request-info-form/model/case-request-info-form.model';
import {
  determineIfDayIsNonWorkingMutation
} from 'app/edozvola/cases/final-document-admission/service/final-document-admission.queries';
import moment from 'moment';
import { Loader } from 'app/shared/loader/loader';
import { getCaseContext } from 'app/shared/util/context-util';

const CaseRequestInfoForm = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { sidebarItemPair } = getCaseContext();

  const { data: requestAmendmentCodebookOptions, isLoading: requestAmendmentCodebookOptionsLoading } = fetchRequestAmendmentCodebookQuery();
  const { data: numberOfDaysToSolveCodebookOptions, isLoading: numberOfDaysToSolveCodebookOptionsLoading } =
    fetchNumberOfDaysToSolveCodebookQuery();

  const { data: caseRequestInfo, isLoading: caseRequestInfoLoading } = fetchCaseRequestInfoQuery(id);
  const { mutate: saveCaseRequestInfo, isPending: savingCaseRequestInfo } = saveCaseRequestInfoMutation(id);
  const { mutate: checkNonWorkingDay, isPending: checkinNonWorkingDays } = determineIfDayIsNonWorkingMutation(value =>
    formik.setFieldValue('solutionDeadlineDate', value),
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: caseRequestInfo || caseRequestInfoInitial,
    validationSchema: caseRequestInfoValidation,
    validateOnMount: true,
    onSubmit: (values: CaseRequestInfoModel) => saveCaseRequestInfo(values),
  });

  const numberOfDaysToSolveChanged = (selectedOption: CodebookModel[]) => {
    checkNonWorkingDay(moment(formik.values.requestCompletionDate).add(Number(selectedOption[0].value), 'days').toDate())
    formik.setValues({
      ...formik.values,
      numberOfDaysToSolve: selectedOption
    });
  };

  return (
    <>
      <FormikProvider value={formik}>
        {caseRequestInfoLoading && <Loader/>}
        {!caseRequestInfoLoading &&
          <Form>
            <FormikValueObserver
              onChange={dirty => {
                if (dirty.dirty) {
                  dispatch(setFormChanged(true));
                } else {
                  dispatch(initCaseFormChange());
                }
              }}
            />
            <h5 className={'form-header'}>Zahtjev</h5>
            <Row>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Naziv akta'} />
                  <Field
                    id="requestName"
                    name="requestName"
                    render={({ field }) => <Input {...field} type="input" error={formik.errors.requestName} disabled={true} />}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Urudžbeni broj'} />
                  <Field
                    id="requestFileNumber"
                    name="requestFileNumber"
                    render={({ field }) => <Input {...field} type="input" error={formik.errors.requestFileNumber} disabled={true} />}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Datum primitka'} />
                  <Field
                    id="requestAdmissionDate"
                    name="requestAdmissionDate"
                    render={({ field }) => (
                      <FormikDatepicker field={field} setFieldValue={formik.setFieldValue} dateFormat={APP_DATE_FORMAT} disabled={true} />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Datum početka rada'} />
                  <Field
                    id="workStartDate"
                    name="workStartDate"
                    render={({ field }) => (
                      <FormikDatepicker
                        error={!sidebarItemPair?.readOnly && (formik.errors.workStartDate as string)}
                        field={field}
                        setFieldValue={formik.setFieldValue}
                        dateFormat={APP_DATE_FORMAT}
                        disabled={sidebarItemPair?.readOnly}
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Dopuna zahtjeva'} />
                  <Field
                    id="requestAmendment"
                    name="requestAmendment"
                    render={({ field }) => (
                      <PaginatedDropdown
                        onItemSelect={selectedOption => formik.setFieldValue('requestAmendment', selectedOption[0])}
                        selected={field.value ?? []}
                        labelKey={'value'}
                        options={requestAmendmentCodebookOptions}
                        placeholder={!sidebarItemPair?.readOnly && 'Odaberi dopunu zahtjeva'}
                        disabled={sidebarItemPair?.readOnly}
                        error={!sidebarItemPair?.readOnly && (formik.errors.requestAmendment as string)}
                        showErrorMessage
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Datum urednog zahtjeva'} />
                  <Field
                    id="requestCompletionDate"
                    name="requestCompletionDate"
                    render={({ field }) => (
                      <FormikDatepicker field={field} setFieldValue={formik.setFieldValue} error={!sidebarItemPair?.readOnly && formik.errors.requestCompletionDate as string} dateFormat={APP_DATE_FORMAT} disabled={true} />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Broj dana za rješavanje'} />
                  <Field
                    id="numberOfDaysToSolve"
                    name="numberOfDaysToSolve"
                    render={({ field }) => (
                      <PaginatedDropdown
                        onItemSelect={numberOfDaysToSolveChanged}
                        selected={field.value ?? []}
                        labelKey={'value'}
                        options={numberOfDaysToSolveCodebookOptions}
                        disabled={sidebarItemPair?.readOnly}
                        error={!sidebarItemPair?.readOnly && (formik.errors.numberOfDaysToSolve as string)}
                        showErrorMessage
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col xs={4}>
                <FormGroup>
                  <Label labelText={'Datum roka rješavanja'} />
                  <Field
                    id="solutionDeadlineDate"
                    name="solutionDeadlineDate"
                    render={({ field }) => (
                      <FormikDatepicker field={field} setFieldValue={formik.setFieldValue} error={!sidebarItemPair?.readOnly && formik.errors.solutionDeadlineDate as string} dateFormat={APP_DATE_FORMAT} disabled={true} />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>

            {!sidebarItemPair?.readOnly && (
              <div className="case-save-button">
                <Button color={'info'} label={'Spremi'} isLoading={savingCaseRequestInfo} size={'lg'} onClick={formik.submitForm} disabled={!formik.dirty || !formik.isValid} />
              </div>
            )}
          </Form>
        }
      </FormikProvider>
    </>
  );
};

export default CaseRequestInfoForm;
